import { useState, useEffect, useRef } from "react";
import { ArrowRightLeft, RefreshCw, Phone } from "lucide-react";
import axios from "axios";
import { decryptRates } from "../utils/crypto";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_NUMBER = "+17787590400";

// Flag paths
const getCurrencyFlag = (code) => {
  const flags = {
    BTC: "/flags/BTC.svg", ETH: "/flags/ETH.svg", USDT: "/flags/USDT.webp",
    ADA: "/flags/ADA.svg", SOL: "/flags/SOL.svg", TRX: "/flags/TRX.webp",
    USD: "/flags/USD.png", EUR: "/flags/EUR.png", GBP: "/flags/GBP.png",
    CAD: "/flags/CAD.png", MXN: "/flags/MXN.png", JPY: "/flags/JPY.png",
    AUD: "/flags/AUD.png", NZD: "/flags/NZD.png", CHF: "/flags/CHF.png",
    CNY: "/flags/CNY.png", INR: "/flags/INR.png", AED: "/flags/AED.png",
    ARS: "/flags/ARS.png", BRL: "/flags/BRL.png", HKD: "/flags/HKD.png",
    SGD: "/flags/SGD.png", KRW: "/flags/KRW.png", THB: "/flags/THB.png",
    PHP: "/flags/PHP.png", IDR: "/flags/IDR.png", MYR: "/flags/MYR.png",
    ZAR: "/flags/ZAR.png", IQD: "/flags/IQD.png", VND: "/flags/VND.png", SEK: "/flags/SEK.png", NOK: "/flags/NOK.png",
    DKK: "/flags/DKK.png", PLN: "/flags/PLN.png", CZK: "/flags/CZK.png",
    HUF: "/flags/HUF.png", ILS: "/flags/ILS.png", TRY: "/flags/TRY.png",
    SAR: "/flags/SAR.png", EGP: "/flags/EGP.png", TWD: "/flags/TWD.png",
    PEN: "/flags/PEN.png", CLP: "/flags/CLP.png", CRC: "/flags/CRC.png",
    FJD: "/flags/FJD.png",
  };
  return flags[code] || null;
};

export default function CurrencyConverter() {
  const [rates, setRates] = useState([]);
  const [amount, setAmount] = useState("1,000");
  const [fromCurrency, setFromCurrency] = useState("USD");
  const [toCurrency, setToCurrency] = useState("CAD");
  const [result, setResult] = useState(null);
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  useEffect(() => {
    fetchRates();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromRef.current && !fromRef.current.contains(event.target)) {
        setShowFromDropdown(false);
      }
      if (toRef.current && !toRef.current.contains(event.target)) {
        setShowToDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchRates = async () => {
    try {
      // Use encrypted endpoint
      const response = await axios.get(`${API}/x7k9`);
      const decryptedRates = decryptRates(response.data.d);
      
      // Check if CAD already exists in the response
      const hasCAD = decryptedRates.some(r => r.currency_code === "CAD");
      let allRates = decryptedRates;
      
      // Only add CAD if it doesn't exist
      if (!hasCAD) {
        allRates = [
          ...decryptedRates,
          { currency_code: "CAD", currency_name: "Canadian Dollar", buy_rate: 1, sell_rate: 1, is_crypto: false }
        ];
      }
      setRates(allRates);
    } catch (error) {
      console.error("Error fetching rates:", error);
    }
  };

  const parseAmount = (str) => {
    return parseFloat(str.replace(/,/g, '')) || 0;
  };

  const formatInputAmount = (value) => {
    const num = value.replace(/[^0-9.]/g, '');
    const parts = num.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const handleConvert = () => {
    setIsConverting(true);
    
    // Small delay for visual feedback
    setTimeout(() => {
      const numAmount = parseAmount(amount);
      if (numAmount <= 0) {
        setResult(null);
        setIsConverting(false);
        return;
      }

      const fromRate = rates.find(r => r.currency_code === fromCurrency);
      const toRate = rates.find(r => r.currency_code === toCurrency);

      if (!fromRate || !toRate) {
        setResult(null);
        setIsConverting(false);
        return;
      }

      // All conversions through CAD. Rates are CAD per 1 unit, named from the customer's side:
      //   sell_rate = what the customer GETS when they sell us a currency (we buy)
      //   buy_rate  = what the customer PAYS when they buy a currency from us (we sell)
      const amountInCAD = numAmount * fromRate.sell_rate;
      let finalAmount = toCurrency === "CAD" ? amountInCAD : amountInCAD / toRate.buy_rate;
      const effectiveRate = finalAmount / numAmount;

      setResult({ amount: finalAmount, rate: effectiveRate });
      setIsConverting(false);
    }, 300);
  };

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setResult(null);
  };

  const formatResult = (num) => {
    return num.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatRate = (num) => {
    return num < 0.01 ? num.toFixed(6) : num.toFixed(4);
  };

  const getCurrencyName = (code) => {
    const rate = rates.find(r => r.currency_code === code);
    return rate ? rate.currency_name : code;
  };

  const sortedCurrencies = [...rates].sort((a, b) => {
    const priority = ["CAD", "USD", "EUR", "GBP", "BTC", "USDT", "ETH"];
    const aIdx = priority.indexOf(a.currency_code);
    const bIdx = priority.indexOf(b.currency_code);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.currency_code.localeCompare(b.currency_code);
  });

  // Filter out the currently selected currency from the opposite dropdown
  const fromDropdownCurrencies = sortedCurrencies.filter(r => r.currency_code !== toCurrency);
  const toDropdownCurrencies = sortedCurrencies.filter(r => r.currency_code !== fromCurrency);

  return (
    <div className="max-w-4xl mx-auto px-4" data-testid="currency-converter">
      {/* Clean white card - no dark header, blends with white section below */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
        
        {/* Converter Row */}
        <div className="flex flex-col lg:flex-row items-stretch gap-4">
          {/* Amount */}
          <div className="flex-1">
            <label className="block text-gray-500 text-xs font-medium mb-2 uppercase tracking-wider">Amount</label>
            <input
              type="text"
              value={amount}
              onChange={(e) => setAmount(formatInputAmount(e.target.value))}
              className="w-full h-14 px-4 text-xl font-semibold text-gray-800 bg-gray-50 border border-gray-200 rounded-lg focus:border-[#001435] focus:ring-2 focus:ring-[#001435]/20 focus:bg-white focus:outline-none transition-all"
              data-testid="converter-amount-input"
            />
          </div>

          {/* From */}
          <div className="flex-1" ref={fromRef}>
            <label className="block text-gray-500 text-xs font-medium mb-2 uppercase tracking-wider">From</label>
            <div className="relative">
              <button
                onClick={() => { setShowFromDropdown(!showFromDropdown); setShowToDropdown(false); }}
                className="w-full h-14 px-4 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 focus:border-[#001435] focus:ring-2 focus:ring-[#001435]/20 focus:outline-none transition-all"
                data-testid="converter-from-select"
              >
                {getCurrencyFlag(fromCurrency) && (
                  <img src={getCurrencyFlag(fromCurrency)} alt={`${fromCurrency} flag`} className="w-7 h-7 rounded-full object-cover" />
                )}
                <span className="text-lg font-semibold text-gray-800">{fromCurrency}</span>
                <svg className="w-4 h-4 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showFromDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto z-50">
                  {fromDropdownCurrencies.map((rate) => (
                    <button
                      key={rate.currency_code}
                      onClick={() => { setFromCurrency(rate.currency_code); setShowFromDropdown(false); setResult(null); }}
                      className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors ${fromCurrency === rate.currency_code ? 'bg-blue-50' : ''}`}
                    >
                      {getCurrencyFlag(rate.currency_code) && (
                        <img src={getCurrencyFlag(rate.currency_code)} alt={`${rate.currency_code} flag`} className="w-6 h-6 rounded-full object-cover" />
                      )}
                      <span className="font-medium text-gray-800">{rate.currency_code}</span>
                      <span className="text-gray-400 text-sm truncate">{rate.currency_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Swap */}
          <div className="flex items-end justify-center lg:pb-0 py-2 lg:py-0">
            <button
              onClick={swapCurrencies}
              className="w-12 h-12 lg:w-10 lg:h-10 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center transition-colors shadow-md"
              data-testid="converter-swap-btn"
            >
              <ArrowRightLeft className="w-5 h-5 lg:w-4 lg:h-4" />
            </button>
          </div>

          {/* To */}
          <div className="flex-1" ref={toRef}>
            <label className="block text-gray-500 text-xs font-medium mb-2 uppercase tracking-wider">To</label>
            <div className="relative">
              <button
                onClick={() => { setShowToDropdown(!showToDropdown); setShowFromDropdown(false); }}
                className="w-full h-14 px-4 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-300 focus:border-[#001435] focus:ring-2 focus:ring-[#001435]/20 focus:outline-none transition-all"
                data-testid="converter-to-select"
              >
                {getCurrencyFlag(toCurrency) && (
                  <img src={getCurrencyFlag(toCurrency)} alt={`${toCurrency} flag`} className="w-7 h-7 rounded-full object-cover" />
                )}
                <span className="text-lg font-semibold text-gray-800">{toCurrency}</span>
                <svg className="w-4 h-4 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showToDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto z-50">
                  {toDropdownCurrencies.map((rate) => (
                    <button
                      key={rate.currency_code}
                      onClick={() => { setToCurrency(rate.currency_code); setShowToDropdown(false); setResult(null); }}
                      className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors ${toCurrency === rate.currency_code ? 'bg-blue-50' : ''}`}
                    >
                      {getCurrencyFlag(rate.currency_code) && (
                        <img src={getCurrencyFlag(rate.currency_code)} alt={`${rate.currency_code} flag`} className="w-6 h-6 rounded-full object-cover" />
                      )}
                      <span className="font-medium text-gray-800">{rate.currency_code}</span>
                      <span className="text-gray-400 text-sm truncate">{rate.currency_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Convert Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleConvert}
            disabled={isConverting || !amount || parseAmount(amount) <= 0}
            className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium text-sm rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="converter-convert-btn"
          >
            {isConverting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Converting...
              </>
            ) : (
              "Convert"
            )}
          </button>
        </div>

        {/* Result */}
        {result && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <p className="text-gray-500 text-sm mb-1">{parseAmount(amount).toLocaleString()} {fromCurrency} =</p>
                <p className="text-3xl md:text-4xl font-bold text-gray-900" data-testid="converter-result">
                  {formatResult(result.amount)} <span className="text-[#001435]">{toCurrency}</span>
                </p>
              </div>
              <div className="md:text-right">
                <p className="text-gray-400 text-sm">1 {fromCurrency} = {formatRate(result.rate)} {toCurrency}</p>
              </div>
            </div>
          </div>
        )}

        {/* Expert Call Banner */}
        <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-blue-500 shrink-0" />
            <span className="text-gray-700 text-sm">Speak with a currency expert today. We can beat competitor rates.</span>
          </div>
          <a
            href={`tel:${PHONE_NUMBER}`}
            className="text-blue-500 hover:text-white hover:bg-blue-500 text-sm font-medium px-4 py-2 border border-blue-500 rounded-lg transition-all w-full sm:w-auto text-center"
          >
            CALL NOW
          </a>
        </div>
      </div>
    </div>
  );
}
